import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { GET_SELLER_STORE } from "../../api/seller-store";
import Header from "../../../../components/common/header";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import PreOrderSalesMethodModal from "../pack-management/sales-method-modal";
import { Order } from "../../../../__generated__/graphql";
import { notify, notifyError } from "../../../../utils/toast";
import OrderCard from "../components/order-card";
import PickupNoticeModal from "../../components/pickup-notice-modal";
import CancelPackOrderModal from "../../components/cancel-pack-order-modal";
import FilterBar from "../components/filter-bar";
import { FilterStatus } from "../components/filter-status-chip";
import { StartPreOrderCancelSelectionButton } from "../components/start-pre-order-cancel-selection-button";
import { clsx } from "clsx";
import { FaHistory } from "react-icons/fa";
import { useCancelSelectionMode } from "../hooks/use-cancel-selection-mode";
import { useCancelOrders } from "../hooks/use-cancel-orders";
import ConfirmOrderModal from "../components/confirm-order-modal";
import CancelOrderConfirmModal from "../components/cancel-order-confirm-modal";
import ErrorPage from "../../../../components/common/error-page";
import { BLOCK_USER } from "../../../../api/blacklist";
import { HiXMark } from "react-icons/hi2";
import { TOGGLE_PICKUP_COMPLETED } from "../../../../api/order-mutations";
import {
  GET_PACK_ORDERS_TODAY_TOMORROW,
  ORDER_FILTERS,
} from "../../api/pack-order-queries";
import {
  CONFIRM_PACK_ORDER,
  CANCEL_PACK_ORDER,
} from "../../api/pack-order-mutations";

const PreOrderSales = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch } = useQuery(
    GET_PACK_ORDERS_TODAY_TOMORROW,
    {
      fetchPolicy: "network-only",
      onError: (error) => {
        notifyError(error.message);
      },
    }
  );
  const {
    data: storeData,
    loading: storeLoading,
    error: storeError,
  } = useQuery(GET_SELLER_STORE, { fetchPolicy: "network-only" });

  const [confirmPackOrder] = useMutation(CONFIRM_PACK_ORDER, {
    refetchQueries: [GET_PACK_ORDERS_TODAY_TOMORROW],
  });

  const [cancelPackOrder] = useMutation(CANCEL_PACK_ORDER, {
    refetchQueries: [GET_PACK_ORDERS_TODAY_TOMORROW],
  });
  const [togglePickupCompleted] = useMutation(TOGGLE_PICKUP_COMPLETED, {
    refetchQueries: [GET_PACK_ORDERS_TODAY_TOMORROW],
  });

  const [isSalesMethodModalOpen, setIsSalesMethodModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [countdown, setCountdown] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterStatus>("ALL"); // 전체 주문 선택 기본값
  const [selectedTab, setSelectedTab] = useState<"TODAY" | "TOMORROW">("TODAY"); // 오늘/내일 탭 선택

  const [showPickupNotice, setShowPickupNotice] = useState(() => {
    return !localStorage.getItem("boss_pickup_notice_seen");
  });

  // 유저 차단 관련 상태
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [blockUser] = useMutation(BLOCK_USER, {
    refetchQueries: [GET_PACK_ORDERS_TODAY_TOMORROW],
  });

  const isTomorrowOrder = (order: Order) => {
    const pickUpDate = new Date(order.packPickupStartTime);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // 내일 날짜로 설정

    return pickUpDate.toDateString() === tomorrow.toDateString(); // 날짜 비교
  };

  // 서버에서 픽업시간을 기준으로 오늘과 내일 주문만 모두 반환하므로 날짜별로 분리
  const allOrders = data?.packOrdersBySellerTodayTomorrow || [];

  const todayOrders = allOrders.filter(
    (order: Order) => !isTomorrowOrder(order)
  );
  const tomorrowOrders = allOrders.filter((order: Order) =>
    isTomorrowOrder(order)
  );

  const todayOrdersPending = todayOrders.filter(
    (order: Order) => !order.isPackConfirmed && !order.isCanceled
  ); // 오늘 픽업 주문 중 확인 필요 주문
  const tomorrowOrdersPending = tomorrowOrders.filter(
    (order: Order) => !order.isPackConfirmed && !order.isCanceled
  ); // 내일 픽업 주문 중 확인 필요 주문

  // 선택된 탭에 따른 주문 목록
  const currentTabOrders =
    selectedTab === "TODAY" ? todayOrders : tomorrowOrders;

  // 필터 적용 및 정렬 (취소된 주문을 아래로)
  const filteredOrders: Order[] = currentTabOrders
    .filter(ORDER_FILTERS[selectedFilter])
    .sort((a: Order, b: Order) => {
      // 취소된 주문을 아래로 정렬
      if (a.isCanceled && !b.isCanceled) return -1;
      if (!a.isCanceled && b.isCanceled) return 1;
      return 0;
    });

  // 선택하여 취소하기 모드 관리
  const {
    isModeStarted: isCancelSelectionModeStarted,
    setIsModeStarted: setIsCancelSelectionModeStarted,
    selectedOrderIds,
    isConfirmModalOpen: isCancelSelectionConfirmModalOpen,
    setIsConfirmModalOpen: setIsCancelSelectionConfirmModalOpen,
    toggleSelection: handleToggleCancelSelection,
    toggleAllSelection: handleToggleAllCancelSelection,
    resetSelectionMode: stopCancelSelectionMode,
  } = useCancelSelectionMode(filteredOrders);

  // 주문 취소 서버 요청
  const { cancelOrders, loading: isConfirmLoading } = useCancelOrders();

  // 주문 취소 함수 (UI 상태도 함께 변경)
  const handleCancelOrders = async () => {
    await cancelOrders(selectedOrderIds);
    setIsCancelSelectionConfirmModalOpen(false);
    setIsCancelSelectionModeStarted(false);
  };

  const 픽업시간30분전까지남은시간 = (reserveTime: any) => {
    const now = new Date();
    const dayOfWeek = ((now.getDay() + 6) % 7).toString(); // 월요일을 0으로, 일요일을 6으로 설정
    const startTime = reserveTime?.[dayOfWeek]?.start;
    if (startTime) {
      const [hours, minutes] = startTime.split(":").map(Number);
      const startDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );
      startDate.setMinutes(startDate.getMinutes() - 30);
      const diff = startDate.getTime() - now.getTime();
      if (diff > 0) {
        const diffHours = Math.floor(diff / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const diffSeconds = Math.floor((diff % (1000 * 60)) / 1000);
        return `${diffHours.toString().padStart(2, "0")}:${diffMinutes
          .toString()
          .padStart(2, "0")}:${diffSeconds.toString().padStart(2, "0")}`;
      }
    }
    return null;
  };
  const 픽업마감시간 = (reserveTime: any) => {
    const now = new Date();
    const dayOfWeek = ((now.getDay() + 6) % 7).toString(); // 월요일을 0으로, 일요일을 6으로 설정
    const endTime = reserveTime?.[dayOfWeek]?.end;
    if (endTime) {
      const [hours, minutes] = endTime.split(":").map(Number);
      const endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );
      return endDate.toLocaleTimeString();
    }
    return null;
  };

  // countdown counter
  useEffect(() => {
    const interval = setInterval(() => {
      const timeLeft = 픽업시간30분전까지남은시간(
        storeData?.getSellerStore?.reserveTime
      );
      setCountdown(timeLeft);
      if (countdown === "00:00:01") {
        window.location.reload();
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeData]);

  if (storeLoading) return <LoadingSpinnerFull />;
  if (storeError) {
    return (
      <ErrorPage
        description="사유: 매장 정보를 가져오는 중 문제가 발생했습니다"
        homePath="/boss/pre-order/home"
      />
    );
  }
  if (!storeData?.getSellerStore) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );
  }

  if (loading) return <LoadingSpinnerFull />;
  if (error) {
    return (
      <ErrorPage
        description="사유: 주문내역을 불러오는 중 문제가 발생했습니다."
        homePath="/boss/pre-order/home"
      />
    );
  }

  if (!storeData?.getSellerStore?.pack?.price) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white">
        <p className="text-lg mb-6 text-gray-700">
          예약판매 팩 설정을 완료해주세요!⭐
        </p>
        <button
          onClick={() => navigate("/boss/pre-order/pack-management")}
          className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          예약판매 설정 완료하기
        </button>

        <img
          alt="random-pack-setting-ex"
          src={require("./../image/random-pack-setting-ex.png")}
          className="mt-4 rounded-lg w-2/3 max-w-md"
        />
        <p className="mt-2 text-md text-gray-500 text-center">
          이 두가지 설정을 완료해주시면 <br />
          소비자에게 노출됩니다!
        </p>
      </div>
    );
  }

  const handleConfirmOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setIsConfirmModalOpen(true);
  };

  const handleCancelOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const handleBlockUser = (userId: number, userName?: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName || "");
    setIsBlockModalOpen(true);
  };

  const selectedDisplayName = selectedUserName || "익명의 히어로";

  const confirmBlockUser = async () => {
    if (selectedUserId !== null) {
      try {
        const result = await blockUser({
          variables: { userId: selectedUserId },
        });

        if (result.data?.blockUser?.ok) {
          notify("유저가 차단되었습니다.");
          setIsBlockModalOpen(false);
          setSelectedUserId(null);
          setSelectedUserName("");
        } else {
          notifyError(
            result.data?.blockUser?.error || "유저 차단에 실패했습니다."
          );
        }
      } catch (err: any) {
        notifyError(err.message || "유저 차단에 실패했습니다.");
      }
    }
  };

  const confirmOrder = () => {
    if (selectedOrderId !== null) {
      confirmPackOrder({
        variables: { orderId: selectedOrderId },
        onCompleted: () => {
          notify("예약팩이 확정되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
          return;
        },
      });
      setIsConfirmModalOpen(false);
    }
  };

  const confirmCancelOrder = () => {
    // MEMO: 2025.07.25부로 사장님이 설정한 메시지로 고정 발송함
    // 취소 사유 선택 모달 제거하고 확인 모달만 띄우게 되어 사유는 따로 입력하지 않음
    if (selectedOrderId !== null) {
      cancelPackOrder({
        variables: { orderId: selectedOrderId, cancelReason: "" },
        onCompleted: () => {
          notify("예약팩이 취소되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
        },
      });
      setIsModalOpen(false);
    }
  };

  const handleCompletePickup = (orderId: number) => {
    togglePickupCompleted({
      variables: { id: orderId, triggeredBy: "seller" },
    });
  };

  return (
    <div className="bg-[#fff] flex flex-col overflow-y-scroll relative">
      {/* 음영처리되는 상단 영역 */}
      <div>
        <Header
          title="예약 판매 현황"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        >
          <Header.RightContent>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-xl font-bold"
              onClick={() => {
                window.location.reload();
              }}
            >
              새로고침
            </button>
          </Header.RightContent>
        </Header>
        <div
          className="bg-blue-500 text-center text-xl text-white cursor-pointer p-2 underline"
          onClick={() => setIsSalesMethodModalOpen(true)}
        >
          행동가이드 (클릭 후 필독)
        </div>

        {/* 오늘/내일 탭 */}
        <div className="bg-white border-b">
          <div className="flex max-w-xl mx-auto">
            <button
              onClick={() => {
                setSelectedTab("TODAY");
                setSelectedFilter("ALL"); // 상단 탭 변경 시 전체 필터 선택
              }}
              className={`flex-1 py-3 text-center font-medium transition-colors ${
                selectedTab === "TODAY"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500"
              }`}
            >
              <span className="relative inline-block">
                오늘 픽업
                {todayOrdersPending.length > 0 && (
                  <span className="absolute top-[-4px] right-[-19px] bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px]">
                    {todayOrdersPending.length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => {
                setSelectedTab("TOMORROW");
                setSelectedFilter("ALL"); // 상단 탭 변경 시 전체 필터 선택
              }}
              className={`flex-1 py-3 text-center font-medium transition-colors ${
                selectedTab === "TOMORROW"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500"
              }`}
            >
              <span className="relative inline-block">
                내일 픽업
                {tomorrowOrdersPending.length > 0 && (
                  <span className="absolute top-[-4px] right-[-19px] bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px]">
                    {tomorrowOrdersPending.length}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col bg-white max-w-xl w-full mx-auto">
          <div className="pt-6 px-3 flex justify-between items-center">
            <h2 className="text-3xl font-bold text-gray-800">
              주문내역{" "}
              <span className="text-md text-gray-500 font-medium">
                {selectedTab === "TODAY" ? "오늘 픽업 예정" : "내일 픽업 예정"}
              </span>
            </h2>

            <span className="text-lg text-gray-500 ml-2 leading-none">
              {selectedFilter !== "ALL" && (
                <>
                  <span className="font-bold">
                    {filteredOrders?.length ?? 0}개
                  </span>{" "}
                  /{" "}
                </>
              )}
              <span className="text-md">전체 {currentTabOrders.length}개</span>
            </span>
          </div>
          <div className="px-3 mb-4 space-y-2">
            <FilterBar
              selectedStatus={selectedFilter}
              onStatusChange={setSelectedFilter}
            />

            {(selectedFilter === "ALL" || selectedFilter === "PENDING") && (
              <StartPreOrderCancelSelectionButton
                onClick={() => {
                  setIsCancelSelectionModeStarted(true);
                  notify("취소하고자 하는 주문을 선택해주세요!");
                }}
              />
            )}
          </div>
        </div>
      </div>
      {/* OrderCard 영역 - 음영처리에서 제외 */}
      {filteredOrders?.length > 0 ? (
        <div
          className={clsx(
            "px-3 pb-3",
            isCancelSelectionModeStarted && "relative z-20"
          )}
        >
          <div className="bg-gray-100 rounded-lg p-4 space-y-4">
            {[...filteredOrders].reverse().map((order: Order) => (
              <OrderCard
                key={order.id}
                order={order}
                countdown={countdown}
                handleConfirmOrder={handleConfirmOrder}
                handleCancelOrder={handleCancelOrder}
                handleCompletePickup={handleCompletePickup}
                isPickupCompleted={order.isPickupCompleted || null}
                isTomorrowOrder={isTomorrowOrder}
                isCancelSelectionModeStarted={isCancelSelectionModeStarted}
                isCancelSelected={selectedOrderIds.includes(order.id)}
                handleToggleCancelSelection={() => {
                  handleToggleCancelSelection(order);
                }}
                onBlockUser={handleBlockUser}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="text-gray-500 text-lg mb-4">
            {selectedTab === "TODAY" ? "오늘" : "내일"} 픽업 예정인 주문이
            없습니다.
          </div>
        </div>
      )}

      {/* 음영처리되는 하단 영역 */}
      <div className="p-3 pb-20">
        <h3 className="text-3xl font-bold mt-4 text-gray-800">이전의 주문</h3>
        <ul className="w-full max-w-md">
          <button
            className="px-6 py-2 mt-4 bg-white shadow-md font-bold border-2 text-black rounded-xl break-keep"
            onClick={() => navigate("/boss/pre-order/previous-sales")}
          >
            <FaHistory className="w-4 h-4 text-blue-400 inline-block mr-1 mb-0.5" />
            이전 주문 보기
          </button>
        </ul>
      </div>

      {/* 선택 모드일 때 전체 화면에 음영 레이어 */}
      {isCancelSelectionModeStarted && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-10"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}

      {/* 선택 모드일 때 하단 고정 액션 바 */}
      {isCancelSelectionModeStarted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-50">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            <div className="flex items-center gap-3">
              <button
                className={clsx(
                  "text-gray-500 px-4 py-2 rounded-lg",
                  selectedOrderIds.length === 0
                    ? "bg-blue-500 text-white"
                    : "bg-gray-300 text-gray-800"
                )}
                onClick={handleToggleAllCancelSelection}
              >
                {selectedOrderIds.length === 0 ? "모두 선택" : "모두 해제"}
              </button>
              <span className="text-gray-600 font-medium">
                {selectedOrderIds.length}개 선택됨
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={stopCancelSelectionMode}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg"
              >
                취소
              </button>
              <button
                onClick={() => setIsCancelSelectionConfirmModalOpen(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg"
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 선택 모드일 때 하단 패딩 추가 */}
      {isCancelSelectionModeStarted && <div className="h-20"></div>}

      {/* 유저 차단 확인 모달 */}
      {isBlockModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                유저를 차단하시겠습니까?
              </h2>
              <button
                onClick={() => {
                  setIsBlockModalOpen(false);
                  setSelectedUserId(null);
                  setSelectedUserName("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            <p className="text-lg text-gray-600 mb-2">
              <span className="font-semibold">{selectedDisplayName}</span>님을
              차단하시겠습니까?
            </p>
            <p className="text-gray-500 text-sm mb-6">
              차단된 유저는 매장이 노출되지 않으며 주문을 할 수 없습니다.
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                onClick={() => {
                  setIsBlockModalOpen(false);
                  setSelectedUserId(null);
                  setSelectedUserName("");
                }}
              >
                취소
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                onClick={confirmBlockUser}
              >
                차단
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <PickupNoticeModal
        isOpen={showPickupNotice}
        onClose={() => {
          setShowPickupNotice(false);
          localStorage.setItem("boss_pickup_notice_seen", "true");
        }}
        localStorageKey="boss_pickup_notice_seen"
      />
      <PreOrderSalesMethodModal
        isOpen={isSalesMethodModalOpen}
        onClose={() => setIsSalesMethodModalOpen(false)}
        storeData={storeData}
      />
      <CancelPackOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmCancelOrder}
        cancelMessage={storeData?.getSellerStore?.pack?.cancelMessage}
        onEditMessage={() => navigate("/boss/pre-order/pack-management")}
      />

      <ConfirmOrderModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmOrder}
        pickupEndTime={픽업마감시간(storeData?.getSellerStore?.reserveTime)}
      />

      <CancelOrderConfirmModal
        isOpen={isCancelSelectionConfirmModalOpen}
        onClose={() => setIsCancelSelectionConfirmModalOpen(false)}
        onConfirm={handleCancelOrders}
        isConfirmLoading={isConfirmLoading}
        selectedOrderCount={selectedOrderIds.length}
      />
    </div>
  );
};

export default PreOrderSales;
